/**
 * Support system types and interfaces
 * Defines structure for adding support elements to nodes
 */

import { Vector3 } from 'three';

/**
 * Support position relative to the node
 * Z-UP Coordinate System: X=East/West, Y=North/South, Z=Up/Down
 */
export type SupportPosition = 
  | 'below'    // Support cube below the node in -Z direction (DOWN)
  | 'above'    // Support cube above the node in +Z direction (UP)
  | 'left'     // Support cube to the left/west in -X direction (WEST)
  | 'right'    // Support cube to the right/east in +X direction (EAST)
  | 'front'    // Support cube in front/north in +Y direction (NORTH)
  | 'back';    // Support cube behind/south in -Y direction (SOUTH)

/**
 * Degrees of Freedom for support constraints
 * Z-UP System: X=East-West, Y=North-South, Z=Up-Down
 * 0 = fixed/constrained, 1 = free, other values = spring constant
 */
export interface DegreesOfFreedom {
  // Translational DOF
  Fx: number;  // X-direction translation (East-West)
  Fy: number;  // Y-direction translation (North-South)
  Fz: number;  // Z-direction translation (Up-Down)
  // Rotational DOF
  Rx: number;  // X-axis rotation (around East-West axis)
  Ry: number;  // Y-axis rotation (around North-South axis)
  Rz: number;  // Z-axis rotation (around vertical axis)
}

/**
 * Support type/constraint type
 */
export type SupportType = 
  | 'fixed'      // Fully fixed support (all directions constrained)
  | 'pinned'     // Pinned support (translation constrained, rotation free)
  | 'roller';    // Roller support (vertical translation constrained)

/**
 * Support configuration interface
 */
export interface SupportConfig {
  id: string;
  nodeIds: string[];
  position: SupportPosition;
  type: SupportType;
  size: number;          // Size factor for the support cube
  color: string;         // Color of the support cube
  opacity: number;       // Transparency of the support cube
  offset: number;        // Distance offset from the node
  dof: DegreesOfFreedom; // Degrees of freedom constraints
}

/**
 * Support creation options
 */
export interface SupportCreationOptions {
  position?: SupportPosition;
  type?: SupportType;
  size?: number;
  color?: string;
  opacity?: number;
  offset?: number;
  dof?: Partial<DegreesOfFreedom>;
}

/**
 * Support visual properties
 */
export interface SupportVisualProps {
  size: number;
  color: string;
  opacity: number;
  position: Vector3;     // World position of the support cube
  nodePosition: Vector3; // Position of the associated node
}

/**
 * Support context menu item configuration
 */
export interface SupportContextMenuItem {
  id: string;
  label: string;
  position: SupportPosition;
  action: (nodeId: string, position: SupportPosition) => void;
}

/**
 * Default DOF configurations for support types
 * Z-UP System: X=East-West, Y=North-South, Z=Up-Down
 */
export const DEFAULT_DOF_CONFIGS: Record<SupportType, DegreesOfFreedom> = {
  fixed: { Fx: 0, Fy: 0, Fz: 0, Rx: 0, Ry: 0, Rz: 0 }, // All constrained - customizable via UI
  pinned: { Fx: 0, Fy: 0, Fz: 0, Rx: 1, Ry: 1, Rz: 1 }, // Translations fixed, rotations free - predefined
  roller: { Fx: 1, Fy: 1, Fz: 0, Rx: 1, Ry: 1, Rz: 1 }  // Z-translation fixed (vertical), X,Y free (horizontal) - predefined
};

/**
 * Default support configuration values
 */
export const DEFAULT_SUPPORT_CONFIG: Omit<SupportConfig, 'id' | 'nodeIds'> = {
  position: 'below',
  type: 'fixed',
  size: 1,              // 1.0 units cube size
  color: '#00FF00',       // Green color
  opacity: 0.6,           // 60% transparency
  offset: 0.1,            // 0.1 units distance from node
  dof: DEFAULT_DOF_CONFIGS.fixed
};

/**
 * Support position display labels
 * Z-UP System: X=East-West, Y=North-South, Z=Up-Down
 */
export const SUPPORT_POSITION_LABELS: Record<SupportPosition, string> = {
  below: 'Below Node (-Z Down)',
  above: 'Above Node (+Z Up)', 
  left: 'Left of Node (-X West)',
  right: 'Right of Node (+X East)',
  front: 'Front of Node (+Y North)',
  back: 'Back of Node (-Y South)'
};

/**
 * Support type display labels
 */
export const SUPPORT_TYPE_LABELS: Record<SupportType, string> = {
  fixed: 'Fixed Support',
  pinned: 'Pinned Support',
  roller: 'Roller Support'
};

/**
 * Support visual configurations for different types
 * Z-UP System: Shapes oriented for vertical Z-axis
 */
export const SUPPORT_VISUAL_CONFIGS: Record<SupportType, { color: string; shape: string }> = {
  fixed: { color: '#00FF00', shape: 'cube' },      // Green cube (all DOF constrained, no arrows)
  pinned: { color: '#FFD700', shape: 'pyramid' },  // Gold pyramid (tip points up +Z, rotational arrows only)
  roller: { color: '#FF6B6B', shape: 'sphere' }    // Red sphere (rolls on XY plane, Fx/Fy free, Fz constrained)
};